/* eslint-disable react/jsx-key */
// No 'use client': Column has no state or handlers, so in server heros its
// heading and rich text render as plain HTML and never hydrate. Client parents
// (PostHero) still bundle it as a client component.
import React from 'react'
import { cn } from '@/utilities/ui'
import RichText from '../RichText'
import { CMSLink } from '../Link'
import { Media } from '../Media'
import { DynamicHeading } from '../DynamicHeading'
import { SocialLinks, type SocialPlatform } from '../SocialLinks'

interface ColumnImage {
  media: any
  imageStyle?: 'rounded' | 'circle'
  scale?: number
  rotation?: number
  offsetX?: string | number
  offsetY?: string | number
}

interface ColumnLink {
  link: any
}

interface ColumnProps {
  heading?: string | null | undefined
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  subheading?: string | null | undefined
  subheadingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  heading_color?: string | null | undefined
  subheading_color?: string | null | undefined
  richText?: any
  useCustomListIcons?: boolean
  fontSize?: string
  fontWeight?: string
  links?: ColumnLink[]
  socialLinks?: { platform: SocialPlatform; url?: string | null; id?: string | null }[]
  image?: ColumnImage
  size: 'full' | 'half'
  /** Set for above-the-fold placements (heros): loads the media eagerly so the
   * LCP image isn't stuck behind loading="lazy". */
  mediaPriority?: boolean
  /** Per-part overrides merged after the defaults (tailwind-merge resolves
   * conflicts), so a hero can set its own type scale without a new component. */
  classNames?: {
    heading?: string
    subheading?: string
    richText?: string
    image?: string
  }
}

// Your original 4 SVGs
const ListIcons = [
  // Pink
  <svg width="40" height="42" viewBox="0 0 40 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.614258" width="40" height="41" rx="10" fill="#FFE9F7" />
    <path
      d="M9 22.2296L15.9474 29.6143L31 13.6143"
      stroke="#FF32B9"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>,

  // Blue
  <svg width="40" height="42" viewBox="0 0 40 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.614258" width="40" height="41" rx="10" fill="#EAF6FF" />
    <path
      d="M9 22.2296L15.9474 29.6143L31 13.6143"
      stroke="#1D9BFF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>,

  // Green
  <svg width="40" height="42" viewBox="0 0 40 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.614258" width="40" height="41" rx="10" fill="#D9FFF7" />
    <path
      d="M9 22.2296L15.9474 29.6143L31 13.6143"
      stroke="#23DCB5"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>,

  // Yellow
  <svg width="40" height="42" viewBox="0 0 40 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.614258" width="40" height="41" rx="10" fill="#FFFAEE" />
    <path
      d="M9 22.2296L15.9474 29.6143L31 13.6143"
      stroke="#FFBD00"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>,
]

export const Column: React.FC<ColumnProps> = ({
  heading,
  headingLevel = 'h2',
  subheading,
  subheadingLevel = 'h3',
  heading_color = 'none',
  subheading_color = 'none',
  richText,
  useCustomListIcons,
  fontSize,
  fontWeight,
  links,
  socialLinks,
  image,
  size,
  mediaPriority,
  classNames,
}) => {
  const imageClasses = image?.media
    ? cn(
        'w-full flex overflow-hidden',
        {
          'rounded-media': image.imageStyle === 'rounded',
          'rounded-full': image.imageStyle === 'circle',
        },
        classNames?.image,
      )
    : ''

  const imageStyles = {
    transform: [
      image?.scale && `scale(${image.scale})`,
      image?.rotation && `rotate(${image.rotation}deg)`,
    ]
      .filter(Boolean)
      .join(' '),
    marginLeft: image?.offsetX,
    marginTop: image?.offsetY,
  }

  // Use your original icons only if enabled in CMS
  const customIcons = useCustomListIcons ? ListIcons : undefined

  return (
    <div className="flex flex-col justify-center h-full text-center items-center md:text-left md:items-start">
      {image?.media && (
        <div style={imageStyles} className="mx-auto md:mx-0 w-full">
          <Media
            resource={image.media}
            className="w-full"
            imgClassName={imageClasses}
            videoClassName={imageClasses}
            priority={mediaPriority}
          />
        </div>
      )}

      {heading && (
        <DynamicHeading
          level={headingLevel}
          className={cn(
            `text-2xl md:text-4xl font-bold `,
            heading_color !== 'none' ? `text-${heading_color}` : '',
            classNames?.heading,
          )}
        >
          {heading}
        </DynamicHeading>
      )}

      {subheading && (
        <DynamicHeading
          level={subheadingLevel}
          className={cn(
            `text-xl mb-6 font-black my-5 ${size === 'full' ? 'text-2xl' : ''}`,
            subheading_color !== 'none' ? `text-${subheading_color}` : '',
            classNames?.subheading,
          )}
        >
          {subheading}
        </DynamicHeading>
      )}

      {richText && (
        <div
          className={cn(
            'max-w-none mx-auto md:mx-0 my-5',
            fontSize || 'text-lg',
            fontWeight || 'font-normal',
            classNames?.richText,
          )}
        >
          <RichText data={richText} enableGutter={false} customIcons={customIcons} />
        </div>
      )}

      {Array.isArray(links) && links.length > 0 && (
        <ul className="flex flex-col sm:flex-row gap-4 items-center justify-center md:justify-start w-full sm:w-auto">
          {links.map(({ link }, i) => {
            return (
              <li key={i} className="w-full sm:w-auto">
                <CMSLink {...link} />
              </li>
            )
          })}
        </ul>
      )}

      {Array.isArray(socialLinks) && socialLinks.length > 0 && (
        <div className="mt-6">
          <SocialLinks links={socialLinks} />
        </div>
      )}
    </div>
  )
}
